import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Eingangsrechnung,
  Ausgangsrechnung,
  OffenePosten,
  PdfUploadResult,
  Dokument,
} from '../types';
import { downloadDateiOderFehler } from '../lib/download';
import { useRechte } from '../hooks/useRechte';
import { darfBelegeSenden, darfKorrekturbelege, darfRechnungFestschreiben } from '../lib/rechte';
import {
  RECHNUNGSARTEN, artLabel, betragAnzeige, bezugText, centText, inCent, istKorrektur, kannGutschreiben,
  kannStornieren, normalisiereArt,
} from '../lib/rechnungsart';
import { FESTSCHREIBEN_FRAGE, fehlerText } from '../lib/belegKarte';
import { kannZuPartnerNavigieren, navigiereZuPartner } from '../lib/navigation';
import BelegAnsichtModal from './BelegAnsichtModal';
import ERechnungDetail, { ERechnungBadges, ERechnungValidierung } from './ERechnungDetail';
import RowActionsMenu from './RowActionsMenu';
import RechnungsartBadge from './RechnungsartBadge';
import GutschriftDialog from './GutschriftDialog';
import StornoDialog from './StornoDialog';
import { useDateiDrop } from '../lib/useDateiDrop';
import './DokumenteOverview.css';
import './RechnungsartDialoge.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

type SubTab = 'upload' | 'eingang' | 'ausgang' | 'archiv';

const STATUS_LABELS: Record<string, string> = {
  EINGEGANGEN: 'Eingegangen',
  GEPRUEFT: 'Geprüft',
  FREIGEGEBEN: 'Freigegeben',
  BEZAHLT: 'Bezahlt',
  REKLAMIERT: 'Reklamiert',
  ENTWURF: 'Entwurf',
  VERSENDET: 'Versendet',
  TEILBEZAHLT: 'Teilbezahlt',
  UEBERFAELLIG: 'Überfällig',
  STORNIERT: 'Storniert',
};

const STATUS_COLORS: Record<string, string> = {
  EINGEGANGEN: 'var(--green-900)',
  GEPRUEFT: 'var(--green-900)',
  FREIGEGEBEN: 'var(--green-500)',
  BEZAHLT: 'var(--green-500)',
  REKLAMIERT: 'var(--red)',
  ENTWURF: 'var(--ink-2)',
  VERSENDET: 'var(--green-900)',
  TEILBEZAHLT: 'var(--gold)',
  UEBERFAELLIG: 'var(--red)',
  STORNIERT: 'var(--ink-2)',
};

const DokumenteOverview: React.FC = () => {
  const [subTab, setSubTab] = useState<SubTab>('eingang');
  const [eingangsrechnungen, setEingangsrechnungen] = useState<Eingangsrechnung[]>([]);
  const [ausgangsrechnungen, setAusgangsrechnungen] = useState<Ausgangsrechnung[]>([]);
  // Filter „Art“ der Ausgangsrechnungen ('' = alle) und Gutschrift-/Storno-Dialog je Zeile
  const [artFilter, setArtFilter] = useState('');
  const [gutschriftFuer, setGutschriftFuer] = useState<Ausgangsrechnung | null>(null);
  const [stornoFuer, setStornoFuer] = useState<Ausgangsrechnung | null>(null);
  const [rechnungMeldung, setRechnungMeldung] = useState<string | null>(null);
  const [dokumente, setDokumente] = useState<Dokument[]>([]);
  const [detailOffenId, setDetailOffenId] = useState<number | null>(null);
  const [offenePosten, setOffenePosten] = useState<OffenePosten | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState<PdfUploadResult | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Inline-PDF-Viewer
  const [downloadFehler, setDownloadFehler] = useState('');
  // Beleg-Dokumentansicht (Ausgangsrechnung, read-only)
  const [belegAnsicht, setBelegAnsicht] = useState<{ url: string; title: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { rechte } = useRechte();

  // E-Mail Dialog (Ausgangsrechnungen)
  const [emailDialogId, setEmailDialogId] = useState<number | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState<Record<number, string>>({});

  // Mahnung Dialog
  const [mahnungDialogId, setMahnungDialogId] = useState<number | null>(null);
  const [mahnungEmailInput, setMahnungEmailInput] = useState('');
  const [mahnungSending, setMahnungSending] = useState(false);
  const [mahnungFeedback, setMahnungFeedback] = useState<Record<number, string>>({});

  useEffect(() => {
    loadOffenePosten();
    if (subTab === 'eingang') loadEingangsrechnungen();
    else if (subTab === 'ausgang') loadAusgangsrechnungen();
    else if (subTab === 'archiv') loadDokumente();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab]);

  // Filterwechsel lädt nur die Ausgangsrechnungen neu (serverseitig per ?rechnungsart=)
  useEffect(() => {
    if (subTab === 'ausgang') loadAusgangsrechnungen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artFilter]);

  const loadOffenePosten = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dokumente/offene-posten`);
      setOffenePosten(res.data);
    } catch {}
  };

  const loadEingangsrechnungen = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/dokumente/eingangsrechnungen`);
      setEingangsrechnungen(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadAusgangsrechnungen = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/dokumente/ausgangsrechnungen`, {
        params: artFilter ? { rechnungsart: artFilter } : undefined,
      });
      setAusgangsrechnungen(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadDokumente = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/dokumente`);
      setDokumente(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async (file: File) => {
    const name = file.name.toLowerCase();
    if (!name.endsWith('.pdf') && !name.endsWith('.xml')) {
      setUploadError('Nur PDF- oder XML-Dateien erlaubt.');
      return;
    }
    setUploadLoading(true);
    setUploadError(null);
    setUploadResult(null);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('hochgeladen_von', 'Benutzer');
    try {
      const res = await axios.post(`${API_URL}/api/dokumente/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult(res.data);
      loadOffenePosten();
    } catch (e: any) {
      setUploadError(e.response?.data?.detail || 'Upload fehlgeschlagen.');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const { dragOver, dropHandlers } = useDateiDrop({
    accept: '.pdf,.xml',
    onFiles: dateien => handleFile(dateien[0]),
    onError: fehler => setUploadError(fehler),
  });

  const freigebenEingangsrechnung = async (id: number) => {
    try {
      await axios.post(`${API_URL}/api/dokumente/eingangsrechnungen/${id}/freigeben`, {
        freigegeben_von: 'Benutzer',
      });
      loadEingangsrechnungen();
      loadOffenePosten();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Fehler bei der Freigabe');
    }
  };

  /** Datei herunterladen; den Namen bestimmt der Server (Content-Disposition). */
  const dateiHerunterladen = async (url: string, bezeichnung: string, filename: string) => {
    setDownloadFehler('');
    const fehler = await downloadDateiOderFehler(`${API_URL}${url}`, filename, bezeichnung);
    if (fehler) setDownloadFehler(fehler);
  };

  const openBelegAnsicht = (url: string, title: string) => {
    setBelegAnsicht({ url: `${API_URL}${url}`, title });
  };

  const openEmailDialog = (id: number) => {
    setEmailDialogId(id);
    setEmailInput('');
  };

  const sendMahnung = async (id: number) => {
    setMahnungSending(true);
    try {
      await axios.post(`${API_URL}/api/email/mahnung/${id}`, { empfaenger: mahnungEmailInput || undefined });
      setMahnungFeedback(prev => ({ ...prev, [id]: 'Mahnung gesendet' }));
      setMahnungDialogId(null);
      loadAusgangsrechnungen();
    } catch (e: any) {
      setMahnungFeedback(prev => ({ ...prev, [id]: e.response?.data?.detail || 'Fehler beim Senden' }));
      setMahnungDialogId(null);
    } finally {
      setMahnungSending(false);
    }
  };

  /** Rechnungsnummer für die Anzeige: Entwürfe haben noch keine (nummer_anzeige = „Entwurf #id“). */
  const rechnungNr = (r: Ausgangsrechnung) =>
    r.nummer_anzeige ?? r.rechnungsnummer ?? `Entwurf #${r.id}`;

  /** Nach Gutschrift-/Storno-Dialog: Meldung zeigen, Liste neu laden. */
  const rechnungDialogFertig = (meldung: string) => {
    setGutschriftFuer(null);
    setStornoFuer(null);
    setRechnungMeldung(meldung);
    loadAusgangsrechnungen();
    loadOffenePosten();
  };

  /** Entwurf verwerfen: noch ohne Rechnungsnummer, daher Löschen statt Storno. */
  const entwurfLoeschen = async (r: Ausgangsrechnung) => {
    if (!window.confirm(`${rechnungNr(r)} verwerfen? Der Entwurf wird gelöscht.`)) return;
    try {
      await axios.delete(`${API_URL}/api/dokumente/ausgangsrechnungen/${r.id}`);
      loadAusgangsrechnungen();
    } catch (e: any) {
      setEmailFeedback(prev => ({
        ...prev,
        [r.id]: fehlerText(e.response?.data?.detail, 'Entwurf konnte nicht verworfen werden'),
      }));
    }
  };

  /** Entwurf festschreiben (Nummer, Status VERSENDET) ohne E-Mail-Versand. */
  const festschreibenOhneMail = async (r: Ausgangsrechnung) => {
    if (!window.confirm(FESTSCHREIBEN_FRAGE)) return;
    try {
      await axios.post(`${API_URL}/api/dokumente/ausgangsrechnungen/${r.id}/festschreiben`);
      setEmailFeedback(prev => ({ ...prev, [r.id]: 'Rechnung festgeschrieben' }));
      loadAusgangsrechnungen();
    } catch (e: any) {
      setEmailFeedback(prev => ({
        ...prev,
        [r.id]: fehlerText(e.response?.data?.detail, 'Festschreiben fehlgeschlagen'),
      }));
    }
  };

  const sendEmailAusgangsrechnung = async (id: number, erneut = false) => {
    setEmailSending(true);
    try {
      await axios.post(`${API_URL}/api/email/ausgangsrechnung/${id}`, {
        empfaenger: emailInput || undefined,
        ...(erneut ? { erneut: true } : {}),
      });
      setEmailFeedback(prev => ({ ...prev, [id]: 'E-Mail gesendet' }));
      setEmailDialogId(null);
      loadAusgangsrechnungen();
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      setEmailFeedback(prev => ({ ...prev, [id]: (typeof detail === 'string' ? detail : detail?.message) || 'Fehler beim Senden' }));
    } finally {
      setEmailSending(false);
    }
  };

  const formatCurrency = (val?: number) =>
    val != null ? `${val.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '–';

  const formatDate = (val?: string) => {
    if (!val) return '–';
    return new Date(val).toLocaleDateString('de-DE');
  };

  return (
    <div className="dokumente-overview">
      {/* Offene Posten Banner */}
      {offenePosten && (
        <div className="op-banner">
          <div className="op-card">
            <span className="op-label">Offene Forderungen</span>
            <span className="op-value">{formatCurrency(offenePosten.ausgang.summe)}</span>
            <span className="op-sub">{offenePosten.ausgang.anzahl} Rechnungen
              {offenePosten.ausgang.ueberfaellig > 0 &&
                <span className="op-alert"> · {offenePosten.ausgang.ueberfaellig} überfällig</span>
              }
            </span>
          </div>
          <div className="op-card">
            <span className="op-label">Offene Verbindlichkeiten</span>
            <span className="op-value">{formatCurrency(offenePosten.eingang.summe)}</span>
            <span className="op-sub">{offenePosten.eingang.anzahl} zur Zahlung freigegeben
              {offenePosten.eingang.ueberfaellig > 0 &&
                <span className="op-alert"> · {offenePosten.eingang.ueberfaellig} überfällig</span>
              }
            </span>
          </div>
        </div>
      )}

      {downloadFehler && <div className="k-alert k-alert--danger">{downloadFehler}</div>}

      {/* Sub-Tabs */}
      <div className="sub-tabs">
        {(['upload', 'eingang', 'ausgang', 'archiv'] as SubTab[]).map(t => (
          <button
            key={t}
            className={`sub-tab ${subTab === t ? 'active' : ''}`}
            onClick={() => setSubTab(t)}
          >
            {t === 'upload' ? 'Rechnung einlesen' :
             t === 'eingang' ? 'Eingangsrechnungen' :
             t === 'ausgang' ? 'Ausgangsrechnungen' :
             'Archiv'}
          </button>
        ))}
      </div>

      {/* Upload Tab */}
      {subTab === 'upload' && (
        <div className="upload-area">
          <div
            className={`dropzone ${dragOver ? 'drag-over' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            {...dropHandlers}
          >
            <div className="dropzone-icon">PDF / XML</div>
            <div className="dropzone-text">
              {uploadLoading
                ? 'Analyse läuft...'
                : 'PDF oder XML hier ablegen oder klicken zum Auswählen'}
            </div>
            <div className="dropzone-sub">
              Rechnungen, Lieferscheine, Auftragsbestätigungen, Zertifikate – E-Rechnungen (ZUGFeRD, XRechnung) auch als XML
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.xml"
            style={{ display: 'none' }}
            onChange={handleFileInput}
          />

          {uploadLoading && (
            <div className="upload-loading">
              <div className="spinner" />
              <span>KI analysiert das Dokument...</span>
            </div>
          )}

          {uploadError && (
            <div className="upload-error">{uploadError}</div>
          )}

          {uploadResult && (
            <div className="upload-result">
              <div className={`result-header ${uploadResult.analyse_erfolgreich ? 'success' : 'warning'}`}>
                <span>{uploadResult.analyse_erfolgreich ? '✓' : '!'}</span>
                <strong>{uploadResult.dateiname}</strong>
                {uploadResult.konfidenz != null &&
                  <span className="konfidenz">Konfidenz: {uploadResult.konfidenz}%</span>
                }
              </div>

              {uploadResult.erkannter_typ && (
                <div className="result-row">
                  <span className="result-label">Dokumenttyp:</span>
                  <span>{uploadResult.erkannter_typ}</span>
                  <ERechnungBadges format={uploadResult.erechnung_format} />
                </div>
              )}

              {uploadResult.erechnung_format && uploadResult.xml_validierung && (
                <div className="result-row">
                  <ERechnungValidierung validierung={uploadResult.xml_validierung} />
                </div>
              )}

              {uploadResult.extrahierte_daten && (
                <div className="result-extracted">
                  <strong>Extrahierte Daten:</strong>
                  <div className="extracted-grid">
                    {[
                      { key: 'lieferant',     label: 'Lieferant:',      value: uploadResult.extrahierte_daten.lieferant_name },
                      { key: 'rechnungsnr',   label: 'Rechnungsnr.:',   value: uploadResult.extrahierte_daten.rechnungsnummer },
                      { key: 'datum',         label: 'Datum:',           value: uploadResult.extrahierte_daten.rechnungsdatum },
                      { key: 'brutto',        label: 'Brutto:',          value: uploadResult.extrahierte_daten.brutto_gesamt != null ? formatCurrency(Number(uploadResult.extrahierte_daten.brutto_gesamt)) : null },
                      { key: 'zahlungsziel',  label: 'Zahlungsziel:',    value: uploadResult.extrahierte_daten.zahlungsziel_tage != null ? `${uploadResult.extrahierte_daten.zahlungsziel_tage} Tage` : null },
                      { key: 'zusammenfassung', label: 'Zusammenfassung:', value: uploadResult.extrahierte_daten.zusammenfassung },
                    ]
                      .filter(f => f.value != null && f.value !== '')
                      .flatMap(f => [
                        <span key={`${f.key}-label`}>{f.label}</span>,
                        <span key={`${f.key}-value`}>{String(f.value)}</span>,
                      ])}
                  </div>
                </div>
              )}

              <div className="result-hint">{uploadResult.hinweis}</div>
            </div>
          )}
        </div>
      )}

      {/* Eingangsrechnungen Tab */}
      {subTab === 'eingang' && (
        <div className="rechnungen-list">
          {loading ? <div className="loading-spinner" /> : (
            eingangsrechnungen.length === 0
              ? <div className="empty-state">Keine Eingangsrechnungen vorhanden.</div>
              : (
                <table className="dokument-table">
                  <thead>
                    <tr>
                      <th>Lieferant</th>
                      <th>Rechnungsnr.</th>
                      <th>Betrag</th>
                      <th>Status</th>
                      <th>Eingang</th>
                      <th>Fällig</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eingangsrechnungen.map(r => {
                      const hatDetails = !!r.erechnung_format || (r.steuer_aufschluesselung?.length ?? 0) > 0;
                      const detailOffen = detailOffenId === r.id;
                      return (
                      <React.Fragment key={r.id}>
                      <tr className={r.hat_abweichungen ? 'has-warning' : ''}>
                        <td>
                          {r.lieferant}
                          {r.hat_abweichungen && <span className="warning-badge" title="Abweichung zur Bestellung">!</span>}
                        </td>
                        <td>
                          <code>{r.rechnungsnummer}</code>
                          <ERechnungBadges format={r.erechnung_format} typCode={r.typ_code} />
                        </td>
                        <td className="amount">{formatCurrency(r.brutto)}</td>
                        <td>
                          <span className="status-badge" style={{ backgroundColor: STATUS_COLORS[r.status] }}>
                            {STATUS_LABELS[r.status] || r.status}
                          </span>
                        </td>
                        <td>{formatDate(r.eingangsdatum)}</td>
                        <td>{formatDate(r.faellig)}</td>
                        <td className="action-cell-multi">
                          {hatDetails && (
                            <button
                              className="action-btn pdf"
                              onClick={() => setDetailOffenId(detailOffen ? null : r.id)}
                              aria-expanded={detailOffen}
                              aria-controls={`erechnung-detail-${r.id}`}
                              aria-label={`${detailOffen ? 'Details ausblenden' : 'Details'} zu Rechnung ${r.rechnungsnummer}`}
                            >
                              {detailOffen ? 'Details ausblenden' : 'Details'}
                            </button>
                          )}
                          {r.status === 'EINGEGANGEN' || r.status === 'GEPRUEFT' ? (
                            <button
                              className="action-btn approve"
                              onClick={() => freigebenEingangsrechnung(r.id)}
                            >
                              Freigeben
                            </button>
                          ) : null}
                        </td>
                      </tr>
                      {hatDetails && detailOffen && (
                        <tr className="erechnung-detail-row" id={`erechnung-detail-${r.id}`}>
                          <td colSpan={7}>
                            <ERechnungDetail
                              format={r.erechnung_format}
                              typCode={r.typ_code}
                              steuer={r.steuer_aufschluesselung}
                              validierung={r.xml_validierung}
                              onXmlHerunterladen={r.erechnung_format && r.dokument_id != null
                                ? () => dateiHerunterladen(
                                    `/api/dokumente/${r.dokument_id}/xml`,
                                    'E-Rechnungs-XML',
                                    `${r.rechnungsnummer || 'e-rechnung'}.xml`)
                                : undefined}
                            />
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )
          )}
        </div>
      )}

      {/* Ausgangsrechnungen Tab */}
      {subTab === 'ausgang' && (
        <div className="rechnungen-list">
          <div className="rd-zeile" style={{ marginBottom: 10 }}>
            <label>
              <span className="rd-hinweis">Art</span>{' '}
              <select
                value={artFilter}
                onChange={e => { setArtFilter(e.target.value); setRechnungMeldung(null); }}
                aria-label="Nach Rechnungsart filtern"
              >
                <option value="">Alle Arten</option>
                {RECHNUNGSARTEN.map(a => <option key={a} value={a}>{artLabel(a)}</option>)}
              </select>
            </label>
          </div>
          {rechnungMeldung && <div className="rd-ok" role="status" style={{ marginBottom: 10 }}>{rechnungMeldung}</div>}
          {loading ? <div className="loading-spinner" /> : (
            ausgangsrechnungen.length === 0
              ? <div className="empty-state">
                  {artFilter
                    ? `Keine Ausgangsrechnungen der Art „${artLabel(artFilter)}“ vorhanden.`
                    : 'Keine Ausgangsrechnungen vorhanden.'}
                </div>
              : (
                <table className="dokument-table">
                  <thead>
                    <tr>
                      <th>Rechnungsnr.</th>
                      <th>Art</th>
                      <th>Kunde</th>
                      <th>Betrag</th>
                      <th>Status</th>
                      <th>Datum</th>
                      <th>Fällig</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ausgangsrechnungen.map(r => {
                      const korrektur = istKorrektur(r.rechnungsart);
                      const bezug = bezugText(r);
                      const zahlbetragAbweichend = !korrektur && r.zahlbetrag != null
                        && inCent(r.zahlbetrag) !== inCent(r.brutto);
                      const artTitel = artLabel(r.rechnungsart);
                      return (
                      <React.Fragment key={r.id}>
                        <tr>
                          <td>
                            <code>{rechnungNr(r)}</code>
                            {bezug && <div className="rd-bezug">{bezug}</div>}
                          </td>
                          <td><RechnungsartBadge art={r.rechnungsart} /></td>
                          <td>
                            {typeof r.kunde_id === 'number' && kannZuPartnerNavigieren() ? (
                              <button type="button" className="k-btn k-btn--ghost k-btn--sm" title="Kundenakte öffnen"
                                      onClick={() => { if (typeof r.kunde_id === 'number') navigiereZuPartner(r.kunde_id); }}>
                                {r.kunde}
                              </button>
                            ) : r.kunde}
                          </td>
                          <td className={`amount${korrektur ? ' rd-betrag--korrektur' : ''}`}>
                            {betragAnzeige(r.brutto, r.rechnungsart)}
                            {zahlbetragAbweichend && (
                              <span className="rd-betrag-zusatz">Zahlbetrag {centText(inCent(r.zahlbetrag) ?? 0)}</span>
                            )}
                          </td>
                          <td>
                            <span className="status-badge" style={{ backgroundColor: STATUS_COLORS[r.status] }}>
                              {STATUS_LABELS[r.status] || r.status}
                            </span>
                          </td>
                          <td>{formatDate(r.datum)}</td>
                          <td>{formatDate(r.faellig)}</td>
                          <td className="action-cell-multi">
                            {emailFeedback[r.id] && (
                              <span className="email-feedback">{emailFeedback[r.id]}</span>
                            )}
                            {mahnungFeedback[r.id] && (
                              <span className="email-feedback">{mahnungFeedback[r.id]}</span>
                            )}
                            <button
                              className="action-btn pdf"
                              onClick={() => dateiHerunterladen(
                                `/api/dokumente/ausgangsrechnungen/${r.id}/pdf`,
                                artTitel,
                                `RE_${rechnungNr(r).replace(/[#\s]+/g, '_')}.pdf`
                              )}
                              title={`${artTitel} als PDF herunterladen`}
                            >
                              PDF
                            </button>
                            <RowActionsMenu
                              actions={[
                                {
                                  label: 'Beleg-Ansicht öffnen',
                                  onClick: () => openBelegAnsicht(
                                    `/api/dokumente/ausgangsrechnungen/${r.id}/beleg-ansicht`,
                                    `${artTitel} ${rechnungNr(r)}`
                                  ),
                                },
                                ...(r.status !== 'STORNIERT'
                                    && (r.status !== 'ENTWURF' || darfRechnungFestschreiben(rechte, r.rechnungsart)
                                        || normalisiereArt(r.rechnungsart) !== 'GUTSCHRIFT')
                                  ? [{
                                      label: r.status === 'ENTWURF' ? 'Per E-Mail senden' : 'Erneut zustellen',
                                      onClick: () => emailDialogId === r.id ? setEmailDialogId(null) : openEmailDialog(r.id),
                                    }]
                                  : []),
                                ...(r.status === 'ENTWURF' && darfRechnungFestschreiben(rechte, r.rechnungsart)
                                  ? [{
                                      label: 'Festschreiben (ohne Mail)',
                                      onClick: () => festschreibenOhneMail(r),
                                    }]
                                  : []),
                                ...(r.status === 'ENTWURF' && darfBelegeSenden(rechte)
                                  ? [{
                                      label: 'Entwurf verwerfen…',
                                      onClick: () => entwurfLoeschen(r),
                                      danger: true,
                                    }]
                                  : []),
                                // Gutschrift/Storno mindern Forderungen: Belege senden UND Zahlungen buchen
                                // (Buchhaltung, Geschäftsführung, Admin) — sonst antwortet das Backend 403.
                                ...(kannGutschreiben(r) && darfKorrekturbelege(rechte)
                                  ? [{
                                      label: 'Gutschrift erstellen…',
                                      onClick: () => {
                                        setStornoFuer(null); setRechnungMeldung(null);
                                        setGutschriftFuer(gutschriftFuer?.id === r.id ? null : r);
                                      },
                                    }]
                                  : []),
                                ...(kannStornieren(r) && darfKorrekturbelege(rechte)
                                  ? [{
                                      label: 'Stornieren…',
                                      onClick: () => {
                                        setGutschriftFuer(null); setRechnungMeldung(null);
                                        setStornoFuer(stornoFuer?.id === r.id ? null : r);
                                      },
                                      danger: true,
                                    }]
                                  : []),
                                ...(r.status === 'UEBERFAELLIG'
                                  ? [
                                      {
                                        label: 'Mahnungs-Vorschau öffnen',
                                        onClick: () => openBelegAnsicht(
                                          `/api/dokumente/ausgangsrechnungen/${r.id}/mahnung-ansicht?stufe=${r.mahnung_1_am ? 2 : 1}`,
                                          `Mahnung ${rechnungNr(r)}`
                                        ),
                                      },
                                      {
                                        label: 'Mahnung senden',
                                        onClick: () => mahnungDialogId === r.id ? setMahnungDialogId(null) : (setMahnungDialogId(r.id), setMahnungEmailInput('')),
                                        danger: true,
                                      },
                                    ]
                                  : []),
                              ]}
                            />
                          </td>
                        </tr>
                        {gutschriftFuer?.id === r.id && (
                          <tr className="email-dialog-row">
                            <td colSpan={8}>
                              <GutschriftDialog
                                bezug={{ id: r.id, nummer: rechnungNr(r) }}
                                onErstellt={rechnungDialogFertig}
                                onAbbrechen={() => setGutschriftFuer(null)}
                              />
                            </td>
                          </tr>
                        )}
                        {stornoFuer?.id === r.id && (
                          <tr className="email-dialog-row">
                            <td colSpan={8}>
                              <StornoDialog
                                rechnung={{ id: r.id, nummer: rechnungNr(r), rechnungsart: r.rechnungsart }}
                                onFertig={rechnungDialogFertig}
                                onAbbrechen={() => setStornoFuer(null)}
                              />
                            </td>
                          </tr>
                        )}
                        {emailDialogId === r.id && (
                          <tr className="email-dialog-row">
                            <td colSpan={8}>
                              <div className="email-inline-dialog">
                                <input
                                  type="email"
                                  placeholder="Empfänger-E-Mail (leer = Partner-E-Mail)"
                                  value={emailInput}
                                  onChange={e => setEmailInput(e.target.value)}
                                  className="email-inline-input"
                                  autoFocus
                                />
                                <button
                                  className="action-btn approve"
                                  onClick={() => sendEmailAusgangsrechnung(r.id, r.status !== 'ENTWURF')}
                                  disabled={emailSending}
                                >
                                  {emailSending ? 'Sendet...' : (r.status === 'ENTWURF' ? 'Senden' : 'Erneut zustellen')}
                                </button>
                                <button
                                  className="action-btn"
                                  onClick={() => setEmailDialogId(null)}
                                >
                                  Abbrechen
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                        {mahnungDialogId === r.id && (
                          <tr className="email-dialog-row">
                            <td colSpan={8}>
                              <div className="email-inline-dialog">
                                <span style={{ fontWeight: 600, color: 'var(--gold)' }}>Mahnung senden für {rechnungNr(r)}</span>
                                <input
                                  type="email"
                                  placeholder="Empfänger-E-Mail (leer = Partner-E-Mail)"
                                  value={mahnungEmailInput}
                                  onChange={e => setMahnungEmailInput(e.target.value)}
                                  className="email-inline-input"
                                  autoFocus
                                />
                                <button
                                  className="action-btn warning"
                                  onClick={() => sendMahnung(r.id)}
                                  disabled={mahnungSending}
                                >
                                  {mahnungSending ? 'Sendet...' : 'Mahnung senden'}
                                </button>
                                <button
                                  className="action-btn"
                                  onClick={() => setMahnungDialogId(null)}
                                >
                                  Abbrechen
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )
          )}
        </div>
      )}

      {/* Archiv Tab */}
      {subTab === 'archiv' && (
        <div className="rechnungen-list">
          {loading ? <div className="loading-spinner" /> : (
            dokumente.length === 0
              ? <div className="empty-state">Keine Dokumente im Archiv.</div>
              : (
                <table className="dokument-table">
                  <thead>
                    <tr>
                      <th>Typ</th>
                      <th>Richtung</th>
                      <th>Dateiname</th>
                      <th>Konfidenz</th>
                      <th>Status</th>
                      <th>Erstellt</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dokumente.map(d => (
                      <tr key={d.id}>
                        <td>{d.typ}</td>
                        <td>
                          <span className={`richtung-badge ${d.richtung.toLowerCase()}`}>
                            {d.richtung === 'INBOUND' ? '↓ Eingang' : '↑ Ausgang'}
                          </span>
                        </td>
                        <td>
                          {d.dateiname}
                          <ERechnungBadges format={d.erechnung_format} />
                        </td>
                        <td>{d.konfidenz != null ? `${d.konfidenz}%` : '–'}</td>
                        <td>{d.status}</td>
                        <td>{formatDate(d.erstellt_am)}</td>
                        <td className="action-cell-multi">
                          <button
                            className="action-btn pdf"
                            onClick={() => dateiHerunterladen(`/api/dokumente/${d.id}/datei`, d.dateiname, d.dateiname)}
                            title="Datei herunterladen"
                          >
                            ⬇ Laden
                          </button>
                          {d.erechnung_format && (
                            <button
                              className="action-btn pdf"
                              onClick={() => dateiHerunterladen(
                                `/api/dokumente/${d.id}/xml`,
                                'E-Rechnungs-XML',
                                `${d.dateiname.replace(/\.[^.]+$/, '')}.xml`)}
                              title="Original-XML der E-Rechnung herunterladen"
                            >
                              XML herunterladen
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
          )}
        </div>
      )}

      {belegAnsicht && (
        <BelegAnsichtModal
          url={belegAnsicht.url}
          title={belegAnsicht.title}
          kontext={{}}
          onClose={() => setBelegAnsicht(null)}
        />
      )}
    </div>
  );
};

export default DokumenteOverview;
